import { MarketTypes, useCreateProductMutation } from "@qw/graphql";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, FieldInput, Form, Icon } from "@qw/ui";
import { type FC, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router";

import { EnabledSwitch } from "./enabled-switch";
import { ProductFormSchema, type ProductFormValues } from "./product-form.types";
import { ProductMetadataFields } from "./product-metadata-fields";

interface ProductMetadataDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCarrierId?: string;
}

const ProductMetadataDialogContent: FC<
  Omit<ProductMetadataDialogProps, "open"> & { isSaving: boolean; setIsSaving: (value: boolean) => void }
> = ({ onOpenChange, isSaving, setIsSaving }) => {
  const navigate = useNavigate();
  const [createProduct] = useCreateProductMutation();
  const { handleSubmit } = useFormContext<ProductFormValues>();

  const createProductWithMetadata = async (values: ProductFormValues) => {
    const result = await createProduct({
      variables: {
        input: {
          name: values.name || "Untitled Product",
          workflow: "",
          fileUploadIds: values.files.map((f) => f.id),
          carrierId: values.carrierId ?? null,
          lineOfBusinessIds: values.lineOfBusinessIds,
          enabled: values.enabled,
          marketType: values.marketType
            ? values.marketType === "bindingAuthority"
              ? MarketTypes.BindingAuthority
              : MarketTypes.Brokerage
            : null,
        },
      },
      refetchQueries: ["Products"],
    });

    return result.data?.createProduct?.id;
  };

  const handleAddWorkflowDetails = async () => {
    setIsSaving(true);
    try {
      await handleSubmit(async (values) => {
        const id = await createProductWithMetadata(values);
        onOpenChange(false);

        if (id) {
          navigate(`/products/${id}`);
        }
      })();
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional
      console.error("Error creating product:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <FieldInput label="Name" name="name" placeholder="Enter product name" />

      <EnabledSwitch />

      <ProductMetadataFields canEdit={true} />

      <DialogFooter>
        <Button type="button" onClick={handleAddWorkflowDetails} disabled={isSaving}>
          {isSaving ? (
            <>
              <Icon icon="sync" className="animate-spin mr-2 h-4 w-4" />
              Creating...
            </>
          ) : (
            <>
              <Icon icon="edit" className="mr-2 h-4 w-4" />
              Save and edit appetite
            </>
          )}
        </Button>
      </DialogFooter>
    </div>
  );
};

export const ProductMetadataDialog: FC<ProductMetadataDialogProps> = ({ open, onOpenChange, defaultCarrierId }) => {
  const [isSaving, setIsSaving] = useState(false);

  const defaultValues: ProductFormValues = {
    name: "",
    workflow: "",
    files: [],
    carrierId: defaultCarrierId,
    lineOfBusinessIds: [],
    enabled: true,
    marketType: undefined,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Create New Product</DialogTitle>
        </DialogHeader>

        <Form
          mode="onChange"
          onSubmit={async () => {}}
          defaultValues={defaultValues}
          validationSchema={ProductFormSchema}
        >
          <ProductMetadataDialogContent onOpenChange={onOpenChange} isSaving={isSaving} setIsSaving={setIsSaving} />
        </Form>
      </DialogContent>
    </Dialog>
  );
};
